import {
	type WoundAssessmentViewModel,
	getWoundAssessmentMeasurementOptions,
	getWoundOptions,
	getWoundIdentificationAssessmentOptions,
	type WoundIdentificationAssessmentViewModel,
	getWoundAssessmentDataOptions,
	type WoundAssessmentDataViewModel,
	getWoundOrdersForOrderOptions
} from '@generated';
import { useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useOrderItems } from '../orders/queries';

function getWoundIdentificationAssessmentOptionsQueryKey(visitId: string) {
	return getWoundIdentificationAssessmentOptions(visitId).queryKey;
}
function getWoundAssessmentDataOptionsQueryKey(woundAssessmentId: string) {
	return getWoundAssessmentDataOptions(woundAssessmentId).queryKey;
}

export function useGetWoundOptions(woundId: string, visitId?: string, woundAssessmentId?: string) {
	const queryClient = useQueryClient();
	return {
		...getWoundOptions(woundId, result => {
			if (visitId) {
				const woundIdentificationAssessmentQueryKey = getWoundIdentificationAssessmentOptionsQueryKey(visitId);
				const woundIdentificationAssessment =
					queryClient.getQueryData<WoundIdentificationAssessmentViewModel>(woundIdentificationAssessmentQueryKey);
				if (woundIdentificationAssessment) {
					queryClient.setQueryData(
						woundIdentificationAssessmentQueryKey,
						produce(woundIdentificationAssessment, draft => {
							const woundIndex = draft.identifiedWounds.findIndex(w => w.id === result.id);
							if (woundIndex !== -1) {
								draft.identifiedWounds[woundIndex] = result;
							}
						})
					);
				}
			} else if (woundAssessmentId) {
				const woundAssessmentQueryKey = getWoundAssessmentDataOptionsQueryKey(woundAssessmentId);
				const woundAssessment = queryClient.getQueryData<WoundAssessmentDataViewModel>(woundAssessmentQueryKey);
				if (woundAssessment) {
					queryClient.setQueryData(
						woundAssessmentQueryKey,
						produce(woundAssessment, draft => {
							draft.wound = result;
						})
					);
				}
			}
		}),
		initialData: () => {
			if (visitId) {
				const woundIdentificationAssessment = queryClient.getQueryData<WoundIdentificationAssessmentViewModel>(
					getWoundIdentificationAssessmentOptionsQueryKey(visitId)
				);
				if (woundIdentificationAssessment) {
					return woundIdentificationAssessment.identifiedWounds.find(w => w.id === woundId);
				}
			} else if (woundAssessmentId) {
				const woundAssessment = queryClient.getQueryData<WoundAssessmentDataViewModel>(
					getWoundAssessmentDataOptionsQueryKey(woundAssessmentId)
				);
				if (woundAssessment) {
					return woundAssessment.wound;
				}
			}
			return undefined;
		},
		initialDataUpdatedAt: () => {
			if (visitId) {
				return queryClient.getQueryState(getWoundIdentificationAssessmentOptionsQueryKey(visitId))?.dataUpdatedAt;
			} else if (woundAssessmentId) {
				return queryClient.getQueryState(getWoundAssessmentDataOptionsQueryKey(woundAssessmentId))?.dataUpdatedAt;
			}
			return undefined;
		}
	};
}

export function useGetWoundAssessmentMeasurementOptions(woundAssessmentId: string, enabled: boolean = true) {
	const queryClient = useQueryClient();
	const woundAssessmentQueryKey = getWoundAssessmentDataOptionsQueryKey(woundAssessmentId);
	return {
		...getWoundAssessmentMeasurementOptions(woundAssessmentId, result => {
			const woundAssessment = queryClient.getQueryData<WoundAssessmentDataViewModel>(woundAssessmentQueryKey);
			if (woundAssessment) {
				queryClient.setQueryData(
					woundAssessmentQueryKey,
					produce(woundAssessment, draft => {
						draft.assessment.measurement = result;
					})
				);
			}
		}),
		initialData: () => {
			const woundAssessment = queryClient.getQueryData<WoundAssessmentViewModel>(woundAssessmentQueryKey);
			if (!woundAssessment) {
				return undefined;
			}
			return woundAssessment.measurement;
		},
		initialDataUpdatedAt: () => queryClient.getQueryState(woundAssessmentQueryKey)?.dataUpdatedAt,
		enabled
	};
}

export function useGetWoundOrders(orderId: string) {
	return useOrderItems(orderId, getWoundOrdersForOrderOptions, (fullOrder, result) => {
		fullOrder.woundOrders = result;
	});
}
